/**
 * LogsViewer.js
 *
 * Unified component for viewing logs from backtests and deployments.
 *
 * WHY THIS EXISTS:
 * - Provides consistent log viewing experience across the application
 * - Supports both backtest and deployment logs via entityType prop
 * - Full-featured: auto-refresh, date filtering, search, pagination, auto-scroll
 *
 * FEATURES:
 * - Auto-refresh toggle (5-second polling)
 * - Auto-scroll to bottom when new logs arrive
 * - Date range filtering (start/end date pickers)
 * - Full-text search
 * - Infinite scroll pagination with next_token
 * - Limit selector (100/500/1000/5000 lines)
 * - Copy to clipboard
 * - Basic (string) and Advanced (events array) display modes
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Spinner, Form, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCopy, faCheck, faTerminal, faChevronDown, faChevronUp,
  faSearch, faTimes, faFilter, faSync,
  faArrowDown, faArrowUp, faExclamationTriangle, faCalendarAlt,
  faSortAmountDown, faSortAmountUp
} from '@fortawesome/free-solid-svg-icons';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchWithAuth } from '../../utils/apiHelpers';
import './LogsViewer.css';

const LogsViewer = ({
  entityId,
  entityType,
  getApiToken,
  expanded = false,
  onToggle,
  hideToggleButton = false,
  className = '',
  enableDateFilter = true,
  enableSearch = true,
  enableAutoRefresh = true,
  enableLimitSelector = true,
  defaultLimit = 500,
  refreshInterval = 5000,
  defaultSortOrder = 'desc' // 'asc' = oldest first, 'desc' = newest first (default)
}) => {
  // Basic state
  const [logs, setLogs] = useState('');
  const [logEvents, setLogEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Advanced filtering state
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [limit, setLimit] = useState(defaultLimit);
  const [nextToken, setNextToken] = useState(null);
  const [hasMoreLogs, setHasMoreLogs] = useState(false);
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [sortOrder, setSortOrder] = useState(defaultSortOrder); // 'asc' or 'desc'

  // Refs for auto-scroll and refresh interval
  const logsStartRef = useRef(null);
  const logsEndRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const containerRef = useRef(null);
  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);
  const nextTokenRef = useRef(null);
  const isLoadingRef = useRef(false);
  const isFirstRender = useRef(true); // Track first render to avoid duplicate fetch on sortOrder change

  // Check if any filters are active
  const hasActiveFilters = searchQuery || startDate || endDate;

  // Get the appropriate API endpoint based on entity type
  const getApiEndpoint = useCallback(() => {
    if (entityType === 'backtest') {
      return `/backtests/${entityId}/logs`;
    }
    return `/deploy/${entityId}/logs`;
  }, [entityId, entityType]);

  // Function to clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setStartDate(null);
    setEndDate(null);
    setNextToken(null);
    setIsAdvancedMode(false);
  };

  // Fetch logs function
  const fetchLogs = useCallback(async (isLoadMore = false, pageToken = null) => {
    if (!entityId || !expanded) {
      setIsLoading(false);
      isLoadingRef.current = false;
      return;
    }

    // Prevent duplicate requests
    if (isLoadingRef.current && isLoadMore) {
      console.log('Already loading, skipping duplicate request');
      return;
    }

    try {
      setIsLoading(true);
      isLoadingRef.current = true;
      setError(null);
      const token = await getApiToken();

      // Build query parameters
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit);
      if (searchQuery) params.append('query', searchQuery);
      if (startDate) {
        const startIso = startDate.toISOString();
        params.append('start_time_iso', startIso);
      }
      if (endDate) {
        const endIso = endDate.toISOString();
        params.append('end_time_iso', endIso);
      }
      // Use the passed pageToken for load more
      if (isLoadMore && pageToken) {
        console.log('Adding next_token to request:', pageToken);
        params.append('next_token', pageToken);
      }
      // Always send sort order to backend so it returns the correct slice of logs
      params.append('sort', sortOrder);
      console.log('[LogsViewer] Fetching logs with sort order:', sortOrder);

      const queryString = params.toString();
      const baseUrl = getApiEndpoint();
      const url = `${baseUrl}${queryString ? `?${queryString}` : ''}`;

      const data = await fetchWithAuth(token, url);

      // Check if we received structured events or simple logs
      if (data.events !== undefined) {
        // Advanced mode with structured events
        setIsAdvancedMode(true);

        // Log what we received from backend
        if (data.events && data.events.length > 0) {
          console.log('[LogsViewer] Received', data.events.length, 'events from backend');
          console.log('[LogsViewer] First event from backend:', data.events[0].message.substring(0, 60));
          console.log('[LogsViewer] Last event from backend:', data.events[data.events.length - 1].message.substring(0, 60));
        }

        // Sort events by timestamp based on sortOrder
        // 'asc' = oldest first (a - b), 'desc' = newest first (b - a)
        const sortFn = sortOrder === 'desc'
          ? (a, b) => b.timestamp - a.timestamp
          : (a, b) => a.timestamp - b.timestamp;
        const sortedEvents = (data.events || []).sort(sortFn);

        if (isLoadMore) {
          // When loading more, merge and sort all events
          setLogEvents(prev => {
            const allEvents = [...prev, ...sortedEvents];
            // Remove duplicates based on timestamp and message
            const uniqueEvents = allEvents.filter((event, index, self) =>
              index === self.findIndex((e) =>
                e.timestamp === event.timestamp && e.message === event.message
              )
            );
            // Sort all events based on current sort order
            return uniqueEvents.sort(sortFn);
          });
        } else {
          setLogEvents(sortedEvents);
        }
        setNextToken(data.next_token);
        setHasMoreLogs(!!data.next_token);
        console.log('Updated nextToken:', data.next_token, 'hasMore:', !!data.next_token);
        setLogs(''); // Clear simple logs when in advanced mode
      } else {
        // Basic mode with simple text logs
        setIsAdvancedMode(false);
        const logsContent = Array.isArray(data.logs) ? data.logs.join('\n') : data.logs || '';
        setLogs(logsContent);
        setLogEvents([]);
        setNextToken(null);
        setHasMoreLogs(false);
      }
    } catch (err) {
      console.error(`Error fetching ${entityType} logs:`, err);
      setError(err.message || "Failed to load logs");
      if (!isLoadMore) {
        setLogs('');
        setLogEvents([]);
      }
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [entityId, entityType, getApiToken, expanded, limit, searchQuery, startDate, endDate, getApiEndpoint, sortOrder]);

  // Sync refs with state to avoid stale closures
  useEffect(() => {
    nextTokenRef.current = nextToken;
  }, [nextToken]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Load more logs - using ref to avoid dependency issues
  const loadMoreLogs = useCallback(() => {
    if (nextTokenRef.current && !isLoadingRef.current) {
      console.log('Loading more with token:', nextTokenRef.current);
      fetchLogs(true, nextTokenRef.current);
    }
  }, [fetchLogs]);

  // Auto-refresh toggle
  const toggleAutoRefresh = () => {
    setAutoRefresh(prev => !prev);
  };

  // Sort order toggle - triggers refetch from backend with new sort order
  // This ensures we get the correct slice of logs (e.g., newest 500 vs oldest 500)
  const toggleSortOrder = () => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    console.log('[LogsViewer] Toggling sort order from', sortOrder, 'to', newOrder);
    setSortOrder(newOrder);
    // Clear existing data and reset pagination to fetch fresh with new sort order
    setLogEvents([]);
    setLogs('');
    setNextToken(null);
    setHasMoreLogs(false);
  };

  // Effect for auto-refresh
  useEffect(() => {
    if (autoRefresh && expanded) {
      refreshIntervalRef.current = setInterval(() => {
        fetchLogs(false);
      }, refreshInterval);
    } else {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh, expanded, fetchLogs, refreshInterval]);

  // Effect to fetch logs when expanded
  useEffect(() => {
    if (expanded) {
      fetchLogs(false);
    } else {
      setIsLoading(false);
    }
  }, [entityId, expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect to refetch logs when sort order changes (skip first render)
  // This ensures we get the correct slice of data from the backend
  useEffect(() => {
    // Skip first render - the expanded useEffect handles initial fetch
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (expanded && entityId) {
      fetchLogs(false);
    }
  }, [sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to show newest logs when auto-refresh is on
  // In 'desc' order (newest first), newest are at top, so scroll to top
  // In 'asc' order (oldest first), newest are at bottom, so scroll to bottom
  useEffect(() => {
    if (autoRefresh) {
      if (sortOrder === 'desc' && logsStartRef.current) {
        logsStartRef.current.scrollIntoView({ behavior: 'smooth' });
      } else if (sortOrder === 'asc' && logsEndRef.current) {
        logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [logs, logEvents, autoRefresh, sortOrder]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = (e) => {
      const element = e.target;
      // Check if scrolled to bottom (with 100px threshold)
      const isNearBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 100;

      // Use refs for real-time state to avoid stale closures
      const canLoadMore = nextTokenRef.current && !isLoadingRef.current && isAdvancedMode;

      if (isNearBottom && canLoadMore) {
        console.log('Infinite scroll triggered:', {
          nextToken: nextTokenRef.current,
          isLoading: isLoadingRef.current,
          isAdvancedMode
        });
        loadMoreLogs();
      }
    };

    // Find the scrollable container
    const scrollContainer = containerRef.current?.querySelector('.logs-viewer-structured');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [isAdvancedMode, loadMoreLogs]);

  // Handle copy functionality
  const handleCopy = async () => {
    let textToCopy = '';

    if (isAdvancedMode && logEvents.length > 0) {
      // Format structured events for copying
      textToCopy = logEvents.map(event => {
        const timestamp = new Date(event.timestamp).toLocaleString();
        return `[${timestamp}] ${event.message}`;
      }).join('\n');
    } else {
      textToCopy = logs;
    }

    if (!textToCopy) return;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy logs:", err);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString() + ' ' + date.toLocaleDateString();
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    setNextToken(null); // Reset pagination
    fetchLogs(false);
  };

  // Handle clear start date
  const handleClearStart = () => {
    setStartDate(null);
    setTimeout(() => {
      if (startDatePickerRef.current) {
        startDatePickerRef.current.setOpen(true);
      }
    }, 0);
  };

  // Handle clear end date
  const handleClearEnd = () => {
    setEndDate(null);
    setTimeout(() => {
      if (endDatePickerRef.current) {
        endDatePickerRef.current.setOpen(true);
      }
    }, 0);
  };

  // Determine container class names
  const containerClassName = `logs-viewer-container ${hideToggleButton ? 'logs-viewer-live' : ''} ${className}`.trim();

  // Render the logs content
  const renderContent = () => (
    <div className="logs-viewer">
      {/* Advanced Filtering Controls */}
      <div className="logs-viewer-filters">
        <Form onSubmit={handleSearch}>
          <div className="logs-filters-grid">
            {/* Search Input */}
            {enableSearch && (
              <div className="logs-filter-group logs-filter-search">
                <label className="logs-filter-label lumi-label">Search</label>
                <div className="logs-search-input-wrapper">
                  <FontAwesomeIcon icon={faSearch} className="logs-search-icon" />
                  <input
                    type="text"
                    className="logs-search-input lumi-input"
                    placeholder="Search logs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="logs-search-clear-btn lumi-clear-button"
                      aria-label="Clear log search"
                      onClick={() => setSearchQuery('')}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Date Range Filters */}
            {enableDateFilter && (
              <div className="logs-filter-group logs-filter-date-range">
                <label className="logs-filter-label lumi-label">Date Range</label>
                <div className="logs-date-pickers-row">
                  <div className="logs-date-picker-wrapper">
                    <FontAwesomeIcon icon={faCalendarAlt} className="logs-date-picker-icon lumi-date-icon" />
                    <DatePicker
                      ref={startDatePickerRef}
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      showTimeSelect
                      dateFormat="MMM d, yyyy h:mm aa"
                      placeholderText="Start date & time"
                      className="logs-date-picker-input lumi-date-input with-icon"
                      maxDate={endDate || new Date()}
                      isClearable={false}
                      calendarClassName="logs-dark-theme-calendar"
                      popperClassName="logs-dark-theme-popper"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                    />
                    {startDate && (
                      <button
                        type="button"
                        className="logs-date-clear-btn lumi-clear-button"
                        aria-label="Clear start date"
                        onClick={handleClearStart}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>

                  <div className="logs-date-picker-wrapper">
                    <FontAwesomeIcon icon={faCalendarAlt} className="logs-date-picker-icon lumi-date-icon" />
                    <DatePicker
                      ref={endDatePickerRef}
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      showTimeSelect
                      dateFormat="MMM d, yyyy h:mm aa"
                      placeholderText="End date & time"
                      className="logs-date-picker-input lumi-date-input with-icon"
                      minDate={startDate}
                      maxDate={new Date()}
                      isClearable={false}
                      calendarClassName="logs-dark-theme-calendar"
                      popperClassName="logs-dark-theme-popper"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                    />
                    {endDate && (
                      <button
                        type="button"
                        className="logs-date-clear-btn lumi-clear-button"
                        aria-label="Clear end date"
                        onClick={handleClearEnd}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Limit Selector */}
            {enableLimitSelector && (
              <div className="logs-filter-group logs-filter-limit">
                <label className="logs-filter-label lumi-label">Limit</label>
                <select
                  className="logs-limit-select lumi-select"
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                >
                  <option value="100">100 lines</option>
                  <option value="500">500 lines</option>
                  <option value="1000">1000 lines</option>
                  <option value="5000">5000 lines</option>
                </select>
              </div>
            )}

            {/* Action Buttons */}
            <div className="logs-filter-group logs-filter-actions">
              <label className="logs-filter-label lumi-label">&nbsp;</label>
              <div className="logs-action-buttons-row">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="logs-filter-apply-btn"
                >
                  <FontAwesomeIcon icon={faFilter} className="logs-btn-icon" />
                  Apply
                </button>

                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    disabled={isLoading}
                    className="logs-filter-clear-btn"
                  >
                    <FontAwesomeIcon icon={faTimes} className="logs-btn-icon" />
                    Clear
                  </button>
                )}

                {enableAutoRefresh && (
                  <button
                    type="button"
                    onClick={toggleAutoRefresh}
                    className={`logs-auto-refresh-btn ${autoRefresh ? 'active' : ''}`}
                  >
                    <FontAwesomeIcon
                      icon={faSync}
                      className={`logs-btn-icon ${autoRefresh ? 'fa-spin' : ''}`}
                    />
                    {autoRefresh ? 'Auto' : 'Manual'}
                  </button>
                )}

                {/* Sort Order Toggle */}
                <button
                  type="button"
                  onClick={toggleSortOrder}
                  className="logs-sort-order-btn"
                  title={sortOrder === 'desc' ? 'Showing newest first. Click for oldest first.' : 'Showing oldest first. Click for newest first.'}
                >
                  <FontAwesomeIcon
                    icon={sortOrder === 'desc' ? faSortAmountDown : faSortAmountUp}
                    className="logs-btn-icon"
                  />
                  {sortOrder === 'desc' ? 'Newest' : 'Oldest'}
                </button>
              </div>
            </div>
          </div>
        </Form>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="logs-active-filters">
            <span className="logs-filters-label">Active filters:</span>
            {searchQuery && (
              <Badge bg="info" className="logs-filter-badge">
                Search: "{searchQuery}"
              </Badge>
            )}
            {startDate && (
              <Badge bg="info" className="logs-filter-badge">
                From: {startDate.toLocaleString()}
              </Badge>
            )}
            {endDate && (
              <Badge bg="info" className="logs-filter-badge">
                To: {endDate.toLocaleString()}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Logs Display Area */}
      {isLoading && !logEvents.length && !logs ? (
        <div className="logs-viewer-loading">
          <Spinner animation="border" size="md" variant="primary" role="status" />
          <div className="logs-loading-text">
            <span className="logs-loading-message">Loading logs</span>
            <span className="logs-loading-dots"></span>
          </div>
        </div>
      ) : error ? (
        <Alert variant="danger" className="logs-viewer-error">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          Error loading logs: {error}
        </Alert>
      ) : (!logs && !logEvents.length) ? (
        <div className="logs-no-logs">
          <FontAwesomeIcon icon={faTerminal} className="me-2" />
          No logs available for this {entityType}.
        </div>
      ) : (
        <div className="logs-viewer-wrapper">
          <button
            className={`logs-viewer-copy-button ${copied ? 'copied' : ''}`}
            onClick={handleCopy}
            disabled={!logs && !logEvents.length}
          >
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
            {copied ? 'Copied!' : 'Copy'}
          </button>

          {/* Display logs based on mode */}
          {isAdvancedMode && logEvents.length > 0 ? (
            <div className="logs-viewer-structured">
              <div ref={logsStartRef} className="logs-start-anchor" />
              {logEvents.map((event, index) => (
                <div key={index} className="logs-event" data-testid="log-event">
                  <span className="logs-timestamp">
                    [{formatTimestamp(event.timestamp)}]
                  </span>
                  <span className="logs-message">{event.message}</span>
                </div>
              ))}
              <div ref={logsEndRef} className="logs-end-anchor" />

              {/* Infinite Scroll Loading Indicator */}
              {hasMoreLogs && (
                <div className="logs-load-more-container">
                  {isLoading ? (
                    <div className="logs-infinite-scroll-loader">
                      <Spinner animation="border" size="sm" variant="primary" role="status" />
                      <span className="logs-loading-text">Loading more logs...</span>
                    </div>
                  ) : (
                    <div className="logs-scroll-hint">
                      <FontAwesomeIcon icon={faArrowDown} className="logs-scroll-hint-icon" />
                      <span className="logs-scroll-hint-text">Scroll down for more</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <pre className="logs-viewer-content">
              <div ref={logsStartRef} className="logs-start-anchor" />
              {logs}
              <div ref={logsEndRef} className="logs-end-anchor" />
            </pre>
          )}
        </div>
      )}
    </div>
  );

  // Render toggle button when not expanded and toggle is enabled
  if (!hideToggleButton && !expanded) {
    return (
      <button className={`logs-viewer-toggle-button ${className}`.trim()} onClick={onToggle}>
        <FontAwesomeIcon icon={faTerminal} className="logs-viewer-icon" />
        <span>View Logs</span>
        <FontAwesomeIcon icon={faChevronDown} className="logs-viewer-chevron" />
      </button>
    );
  }

  // Render content based on expanded state
  if (hideToggleButton) {
    return expanded ? (
      <div className={containerClassName} ref={containerRef}>
        {renderContent()}
      </div>
    ) : null;
  }

  return (
    <div className={containerClassName} ref={containerRef}>
      <button className="logs-viewer-toggle-button" onClick={onToggle}>
        <FontAwesomeIcon icon={faTerminal} className="logs-viewer-icon" />
        <span>{expanded ? 'Hide Logs' : 'View Logs'}</span>
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="logs-viewer-chevron" />
      </button>
      {renderContent()}
    </div>
  );
};

LogsViewer.propTypes = {
  /** The ID of the entity (backtest or deployment) */
  entityId: PropTypes.string.isRequired,
  /** The type of entity - determines which API endpoint to use */
  entityType: PropTypes.oneOf(['backtest', 'deploy']).isRequired,
  /** Function to get the API authentication token */
  getApiToken: PropTypes.func.isRequired,
  /** Whether the logs section is expanded/visible */
  expanded: PropTypes.bool,
  /** Callback when the toggle button is clicked */
  onToggle: PropTypes.func,
  /** Whether to hide the toggle button (for embedded use) */
  hideToggleButton: PropTypes.bool,
  /** Additional CSS class names */
  className: PropTypes.string,
  /** Whether to enable date range filtering */
  enableDateFilter: PropTypes.bool,
  /** Whether to enable search functionality */
  enableSearch: PropTypes.bool,
  /** Whether to enable auto-refresh toggle */
  enableAutoRefresh: PropTypes.bool,
  /** Whether to enable limit selector */
  enableLimitSelector: PropTypes.bool,
  /** Default limit for log entries */
  defaultLimit: PropTypes.number,
  /** Auto-refresh interval in milliseconds */
  refreshInterval: PropTypes.number,
  /** Default sort order: 'desc' (newest first) or 'asc' (oldest first) */
  defaultSortOrder: PropTypes.oneOf(['asc', 'desc']),
};

export default LogsViewer;
